/* eslint-disable */
// ============================================================
//  UTILS - Fonctions utilitaires
// ============================================================

import { useState } from "react";
import { supabase } from "./supabase";
import {T,
  CAT_PERMISSIONS,
  COUNTRY_NEIGHBORS,
  COUNTRY_COORDS,
  REGION_COORDS,
  FORBIDDEN_KEYWORDS,
  BASE_CRITERIA,
  EXTRA_CRITERIA, GOOGLE_TRANSLATE_KEY} from "./constants";

export function useLang(lang) {
  return T[lang] || T.en || T.fr;  // Fallback EN puis FR si EN n'existe pas
}

export function getAllowedCats(userCats) {
  if(!userCats||userCats.length===0) return [];
  const allowed = new Set();
  userCats.forEach(cat => {
    const perms = CAT_PERMISSIONS[cat] || [];
    perms.forEach(p => allowed.add(p));
  });
  return Array.from(allowed);
}

export function validatePassword(pwd) {
  if (!pwd) pwd = "";
  const checks = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    digit: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd)
  };
  const valid = checks.length && checks.upper && checks.lower && checks.digit && checks.special;
  const score = Object.values(checks).filter(Boolean).length;
  return { ...checks, valid, score };
}

export function getProximityScore(ad, userCountry, userRegion) {
  if (!ad) return 0;
  if (!ad.country) return 50;
  if (userCountry && ad.country === userCountry && userRegion) {
    if (ad.region === userRegion) return 1000;
    if ((ad.coverageRegions || []).includes(userRegion)) return 950;
  }
  if (userCountry && ad.country === userCountry) return 500;
  if (userCountry && (COUNTRY_NEIGHBORS[userCountry] || []).includes(ad.country)) return 100;
  return 10;
}

export function getAdCoords(ad) {
  // Si l'annonce a deja des coords stockees (apres geocoding), les utiliser
  if (ad.lat && ad.lng) {
    return [parseFloat(ad.lat), parseFloat(ad.lng)];
  }
  if (ad.coords && Array.isArray(ad.coords) && ad.coords.length === 2) {
    return ad.coords;
  }
  
  // Helper pour normaliser (enlever accents)
  const norm = (s) => s ? s.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : "";
  
  // Generer un offset deterministe a partir de l'id pour eviter le random a chaque render
  const seed = ad.id ? String(ad.id).split('').reduce((a,c)=>a + c.charCodeAt(0), 0) : 0;
  const offsetLat = ((seed * 7919) % 100 - 50) / 1000;  // -0.05 to 0.05
  const offsetLng = ((seed * 5381) % 100 - 50) / 1000;
  
  if (ad.region) {
    // Essayer d'abord avec accents
    if (REGION_COORDS[ad.region]) {
      const c = REGION_COORDS[ad.region];
      return [c[0] + offsetLat, c[1] + offsetLng];
    }
    // Sinon essayer sans accents
    const normalized = norm(ad.region);
    if (REGION_COORDS[normalized]) {
      const c = REGION_COORDS[normalized];
      return [c[0] + offsetLat, c[1] + offsetLng];
    }
    // Chercher en comparant sans accents
    for (const key in REGION_COORDS) {
      if (norm(key).toLowerCase() === normalized.toLowerCase()) {
        const c = REGION_COORDS[key];
        return [c[0] + offsetLat, c[1] + offsetLng];
      }
    }
  }
  if (ad.country && COUNTRY_COORDS[ad.country]) {
    const c = COUNTRY_COORDS[ad.country];
    return [c[0] + offsetLat * 5, c[1] + offsetLng * 5];
  }
  return null;
}

// Geocoder une ville via OpenStreetMap Nominatim
// Retourne [lat, lng] ou null
export async function geocodeCity(city, region, country) {
  if (!city) return null;
  try {
    const query = [city, region, country].filter(Boolean).join(', ');
    const url = 'https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(query) + '&limit=1';
    const res = await fetch(url, {
      headers: { 'User-Agent': 'HorseConnex/1.0' }
    });
    const data = await res.json();
    if (data && data.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch (err) {
    console.error('Geocoding error:', err);
  }
  return null;
}

export const moderateContent = async (text) => {
  if (!text || typeof text !== "string") return { status: "approved", flags: [], score: 0 };
  const lower = text.toLowerCase();
  const flags = [];
  let score = 0;
  for (const word of FORBIDDEN_KEYWORDS) {
    if (lower.indexOf(word) !== -1) {
      flags.push("profanity");
      score += 30;
      break;
    }
  }
  const upperRatio = (text.match(/[A-Z]/g) || []).length / Math.max(text.length, 1);
  if (text.length > 30 && upperRatio > 0.6) {
    flags.push("spam");
    score += 20;
  }
  if (/https?:\/\/(?!horseconnex|vercel\.app)\S+/i.test(text)) {
    flags.push("suspicious");
    score += 25;
  }
  let status = "approved";
  if (score >= 50) status = "flagged";
  if (score >= 80) status = "rejected";
  return { status, flags: [...new Set(flags)], score };
}

export const detectLanguage = (text) => {
  if (!text || text.length < 10) return null;
  const lower = text.toLowerCase();
  
  // Mots typiques par langue
  const patterns = {
    fr: /\b(le|la|les|un|une|des|de|du|et|est|pour|avec|dans|sur|cheval|jument|hongre|étalon|vente|à vendre|écurie|cavalier|monte)\b/g,
    en: /\b(the|a|an|and|is|for|with|in|on|horse|mare|gelding|stallion|sale|for sale|stable|rider|riding)\b/g,
    de: /\b(der|die|das|ein|eine|und|ist|für|mit|in|auf|pferd|stute|wallach|hengst|verkauf|zu verkaufen|stall|reiter)\b/g,
    es: /\b(el|la|los|las|un|una|y|es|para|con|en|caballo|yegua|capon|semental|venta|en venta|cuadra|jinete)\b/g,
    it: /\b(il|la|i|le|un|una|e|è|per|con|in|cavallo|cavalla|castrone|stallone|vendita|in vendita|scuderia|cavaliere)\b/g,
    pt: /\b(o|a|os|as|um|uma|e|é|para|com|em|cavalo|égua|castrado|garanhão|venda|à venda|cocheira|cavaleiro)\b/g,
    nl: /\b(de|het|een|en|is|voor|met|in|paard|merrie|ruin|hengst|verkoop|te koop|stal|ruiter)\b/g
  };
  
  let bestLang = null;
  let bestScore = 0;
  for (const [lang, pattern] of Object.entries(patterns)) {
    const matches = lower.match(pattern);
    const score = matches ? matches.length : 0;
    if (score > bestScore) {
      bestScore = score;
      bestLang = lang;
    }
  }
  return bestScore >= 2 ? bestLang : null;
}

export const translateText = async (text, sourceLang, targetLang) => {
  if (!text || sourceLang === targetLang) return text;
  try {
    // 1. Verifier le cache Supabase
    const { data: cached } = await supabase
      .from("translations_cache")
      .select("translated_text")
      .eq("source_lang", sourceLang)
      .eq("target_lang", targetLang)
      .eq("source_text", text)
      .limit(1);
    if (cached && cached.length > 0) {
      return cached[0].translated_text;
    }
    
    // 2. Appel a Google Translate API
    const url = "https://translation.googleapis.com/language/translate/v2?key=" + GOOGLE_TRANSLATE_KEY;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        q: text,
        source: sourceLang,
        target: targetLang,
        format: "text"
      })
    });
    
    if (!res.ok) {
      console.warn("Google Translate error:", res.status);
      return text;
    }
    
    const data = await res.json();
    const translated = data?.data?.translations?.[0]?.translatedText;
    
    if (!translated) return text;
    
    // 3. Sauver dans le cache Supabase
    if (text.length <= 5000) {
      try {
        await supabase.from("translations_cache").insert({
          source_text: text,
          source_lang: sourceLang,
          target_lang: targetLang,
          translated_text: translated
        });
      } catch(err) { /* Ignore cache errors */ }
    }
    
    return translated;
  } catch(err) {
    console.error("Translation error:", err);
    return text;
  }
};

export function getCriteria(userType) {
  const base = BASE_CRITERIA;
  if(!userType) return base;
  const t = userType.toLowerCase();
  if(t.includes("cavalier") || t.includes("pension")) return [...base, ...EXTRA_CRITERIA.cavalier];
  if(t.includes("vétér") || t.includes("ostéo") || t.includes("masseur") || t.includes("kiné") || t.includes("dentiste") || t.includes("acupuncture") || t.includes("maréchal")) return [...base, ...EXTRA_CRITERIA.pro_soin];
  if(t.includes("professeur") || t.includes("entraîneur")) return [...base, ...EXTRA_CRITERIA.pro_ecole];
  if(t.includes("écurie") || t.includes("ecurie")) return [...base, ...EXTRA_CRITERIA.pro_ecurie];
  if(t.includes("transport")) return [...base, ...EXTRA_CRITERIA.transport];
  return [...base, ...EXTRA_CRITERIA.default];
}


// ============================================================
// COMPRESSION IMAGE
// Redimensionne et compresse une image via Canvas (sans lib externe)
// Args:
//   file: File object (depuis input type="file")
//   maxWidth: largeur max en pixels (default 1920)
//   quality: qualite JPEG entre 0 et 1 (default 0.85)
// Returns: Promise<File> compressé
// ============================================================
export function compressImage(file, maxWidth = 1920, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      resolve(file); // pas une image, on retourne tel quel
      return;
    }
    
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Erreur lecture fichier"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Erreur chargement image"));
      img.onload = () => {
        try {
          // Calculer nouvelles dimensions en gardant le ratio
          let { width, height } = img;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          // Dessiner sur canvas
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#FFFFFF"; // fond blanc pour les PNG transparents
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir en blob compresse (toujours JPEG pour mieux compresser)
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Erreur compression"));
                return;
              }
              // Recreer un File avec le nom original mais en JPEG
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, ".jpg"),
                { type: "image/jpeg", lastModified: Date.now() }
              );
              resolve(compressedFile);
            },
            "image/jpeg",
            quality
          );
        } catch (err) {
          reject(err);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ============================================================
// COMPRESSION AVANT UPLOAD (à utiliser partout où on envoie une image)
//
// Une photo de téléphone (4000x3000) pèse ~4 Mo en JPEG mais occupe ~48 Mo
// une fois décodée en mémoire pour l'affichage. Envoyer l'original, c'est
// donc de la data gaspillée à l'upload ET au téléchargement, et de la
// mémoire en trop sur les listes (plusieurs photos décodées en même temps).
//
// Contrairement à compressImage(), cette fonction ne rejette JAMAIS :
// au moindre problème elle rend le fichier d'origine, pour qu'un navigateur
// exotique ou une image corrompue ne puisse pas casser une publication.
//
// keepAlpha : pour les logos. Sans lui, un PNG transparent recevrait un fond
// blanc — invisible sur fond clair, mais rectangle blanc en thème sombre.
// ============================================================
export function compressForUpload(file, opts = {}) {
  const { maxWidth = 1600, quality = 0.82, keepAlpha = false } = opts;

  const readAsImage = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("read"));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("decode"));
        img.onload = () => resolve(img);
        img.src = e.target.result;
      };
      reader.readAsDataURL(f);
    });

  const toBlob = (canvas, type, q) =>
    new Promise((resolve) => canvas.toBlob(resolve, type, q));

  return (async () => {
    try {
      if (!file || !file.type || !file.type.startsWith("image/")) return file;
      // Les GIF (potentiellement animés) et les SVG passent tels quels.
      if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

      const img = await readAsImage(file);
      let { width, height } = img;
      if (!width || !height) return file;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      if (!keepAlpha) {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);

      let type = keepAlpha ? "image/webp" : "image/jpeg";
      let blob = await toBlob(canvas, type, quality);
      // WebView ancien : toBlob peut ne pas connaître le WebP et rendre null.
      if (!blob && keepAlpha) { type = "image/png"; blob = await toBlob(canvas, type); }
      if (!blob) return file;
      // Une image déjà optimisée peut grossir en la ré-encodant : on garde alors l'originale.
      if (blob.size >= file.size) return file;

      const ext = type === "image/webp" ? ".webp" : type === "image/png" ? ".png" : ".jpg";
      return new File([blob], file.name.replace(/\.[^.]+$/, "") + ext, {
        type,
        lastModified: Date.now(),
      });
    } catch (e) {
      return file;
    }
  })();
}

// ============================================
// === DISPLAY NAME - Nom d'affichage utilisateur ===
// ============================================

/**
 * Retourne le nom a afficher pour un utilisateur
 * Si l'utilisateur a active 'use_display_name' ET a un 'display_name', on l'affiche
 * Sinon, on affiche prenom + nom
 * @param {object} profile - Le profil utilisateur
 * @returns {string} Le nom a afficher
 */
export function getDisplayName(profile) {
  if (!profile) return "Utilisateur";
  
  // Si l'utilisateur a active le mode "nom commercial" et a un display_name
  if (profile.use_display_name && profile.display_name && profile.display_name.trim()) {
    return profile.display_name.trim();
  }
  
  // Sinon, prenom + nom (comportement par defaut)
  const fullName = ((profile.prenom || "") + " " + (profile.nom || "")).trim();
  return fullName || "Utilisateur";
}

/**
 * Transforme un lien YouTube ou Vimeo en URL d'embed (iframe). Renvoie null si
 * le lien n'est pas reconnu. Sert a afficher la video dans la fiche d'annonce.
 * @param {string} url
 * @returns {string|null}
 */
export function getVideoEmbed(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  // YouTube : watch?v=, youtu.be/, embed/, shorts/, live/
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) return "https://www.youtube.com/embed/" + m[1];
  // Vimeo : vimeo.com/123456789 ou /video/123456789
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return "https://player.vimeo.com/video/" + m[1];
  return null;
}

/**
 * Retourne les initiales pour un utilisateur (pour avatar)
 * @param {object} profile - Le profil utilisateur
 * @returns {string} Les initiales (max 2 caracteres)
 */
export function getDisplayInitials(profile) {
  if (!profile) return "U";
  
  // Si nom commercial actif, prendre les 2 premieres lettres
  if (profile.use_display_name && profile.display_name && profile.display_name.trim()) {
    const name = profile.display_name.trim();
    return name.substring(0, 2).toUpperCase();
  }
  
  // Sinon, initiales prenom + nom
  const init = ((profile.prenom?.[0] || "") + (profile.nom?.[0] || "")).toUpperCase();
  return init || "U";
}


// --- Tri par distance reelle (position connue uniquement, JAMAIS de prompt ici) ---
export function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
  const s = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1*rad) * Math.cos(lat2*rad) * Math.sin(dLng/2) * Math.sin(dLng/2);
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Position de l'utilisateur SANS declencher de demande de permission :
// - si la geoloc a deja ete accordee (ex: via les cartes), on la lit silencieusement
// - sinon on retombe sur la derniere position connue (cache 24h)
// - sinon null -> les ecrans gardent leur tri habituel
export async function getKnownPosition() {
  let cached = null;
  try {
    const raw = localStorage.getItem("hc_last_pos");
    if (raw) { const p = JSON.parse(raw); if (p && p.lat && Date.now() - (p.ts||0) < 24*3600*1000) cached = p; }
  } catch(e) {}
  try {
    if (navigator.permissions && navigator.geolocation) {
      const st = await navigator.permissions.query({ name: "geolocation" });
      if (st && st.state === "granted") {
        const pos = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (g) => resolve({ lat: g.coords.latitude, lng: g.coords.longitude, ts: Date.now() }),
            () => resolve(null),
            { maximumAge: 600000, timeout: 6000 }
          );
        });
        if (pos) { try { localStorage.setItem("hc_last_pos", JSON.stringify(pos)); } catch(e) {} return pos; }
      }
    }
  } catch(e) {}
  return cached;
}

// Comparateur "le plus proche d'abord" (coords reelles ou centre de region via
// getAdCoords). Sans coords des deux cotes -> fallback (tri habituel).
export function compareByDistance(a, b, pos, fallback) {
  let ca = null, cb = null;
  try { ca = getAdCoords(a); } catch(e) {}
  try { cb = getAdCoords(b); } catch(e) {}
  if (ca && cb) {
    const d = distanceKm(pos.lat, pos.lng, ca[0], ca[1]) - distanceKm(pos.lat, pos.lng, cb[0], cb[1]);
    if (d) return d;
    return fallback ? fallback(a, b) : 0;
  }
  if (ca) return -1;
  if (cb) return 1;
  return fallback ? fallback(a, b) : 0;
}

// ============================================================
//  LIENS DE PARTAGE — pages SEO publiques
// ============================================================
//  Les trois familles de pages referencables (/a/ annonces,
//  /p/ fiches Pro, /t/ trajets HorseRoad) sont rendues cote
//  serveur par api/ dans le repo de PROD. Un lien partage doit
//  pointer sur elles : c'est ce qui donne un vrai apercu avec
//  photo et titre sur Facebook, WhatsApp ou iMessage. Le lien
//  d'app ("/?pro=", "/?trip=") ne montre que l'apercu generique.
//
//  ⚠️ Le domaine est ECRIT EN DUR, volontairement, et surtout
//  PAS window.location.origin : les routes /a/, /p/ et /t/
//  n'existent QUE sur la prod (api/ et vercel.json sont
//  prod-only). Un partage depuis le site de dev produirait
//  sinon un lien mort. Meme parti pris que le partage
//  d'annonce, en place depuis juillet.
// ============================================================

const SHARE_ORIGIN = "https://www.horseconnex.com";

// ⚠️ Doit rester IDENTIQUE au slugify des fonctions api/ (a, p, t)
// et de api/sitemap.js. Un slug divergent n'est pas grave (le
// serveur redirige en 301 vers la bonne URL) mais fait perdre
// un aller-retour, et brouille les statistiques.
export function slugify(s, fallback = "") {
  return String(s || fallback)
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 60)
    .replace(/-+$/g, "");
}

// Une ville saisie a la main arrive avec des espaces en trop et une casse
// variable : "Monnaz", "Monnaz " et "monnaz " comptaient pour trois villes
// distinctes, ce qui fausse tout regroupement geographique et le geocodage.
export function normalizeCity(s) {
  const v = String(s == null ? "" : s).replace(/\s+/g, " ").trim();
  if (!v) return "";
  if (v !== v.toLowerCase() && v !== v.toUpperCase()) return v;
  return v.toLocaleLowerCase()
    .replace(/(^|[\s'’-])(\p{L})/gu, (m, sep, c) => sep + c.toLocaleUpperCase());
}

// Le champ « pays » de l'inscription est du texte libre : on y trouve aussi
// bien "Suisse" que "België" ou "España". La colonne profiles.country attend
// un code ISO-2, et elle pilote le tri par proximite. Sans conversion elle
// restait a sa valeur par defaut pour tout le monde.
// Renvoie "" si le pays n'est pas un des 21 pays servis par l'app : mieux vaut
// aucun pays qu'un pays faux, qui biaiserait le classement des annonces.
const PAYS_VERS_CODE = {
  ch:"CH", suisse:"CH", schweiz:"CH", svizzera:"CH", svizra:"CH", switzerland:"CH", suiza:"CH", suica:"CH", zwitserland:"CH",
  fr:"FR", france:"FR", frankreich:"FR", francia:"FR", franca:"FR", frankrijk:"FR",
  be:"BE", belgique:"BE", belgie:"BE", belgien:"BE", belgium:"BE", belgio:"BE", belgica:"BE",
  nl:"NL", "pays-bas":"NL", "pays bas":"NL", paysbas:"NL", nederland:"NL", niederlande:"NL", netherlands:"NL", holland:"NL", hollande:"NL", "paises bajos":"NL",
  de:"DE", allemagne:"DE", deutschland:"DE", germany:"DE", germania:"DE", alemania:"DE", alemanha:"DE", duitsland:"DE",
  gb:"GB", uk:"GB", "royaume-uni":"GB", "royaume uni":"GB", "united kingdom":"GB", angleterre:"GB", england:"GB", grossbritannien:"GB",
  ie:"IE", irlande:"IE", ireland:"IE", irland:"IE", irlanda:"IE", ierland:"IE",
  it:"IT", italie:"IT", italia:"IT", italy:"IT", italien:"IT",
  es:"ES", espagne:"ES", espana:"ES", spain:"ES", spanien:"ES", spagna:"ES", spanje:"ES",
  pt:"PT", portugal:"PT", portugalia:"PT",
  at:"AT", autriche:"AT", osterreich:"AT", austria:"AT", oostenrijk:"AT",
  se:"SE", suede:"SE", sverige:"SE", sweden:"SE", schweden:"SE", suecia:"SE",
  no:"NO", norvege:"NO", norge:"NO", norway:"NO", norwegen:"NO", noruega:"NO",
  dk:"DK", danemark:"DK", danmark:"DK", denmark:"DK", dinamarca:"DK",
  pl:"PL", pologne:"PL", polska:"PL", poland:"PL", polen:"PL", polonia:"PL",
  lu:"LU", luxembourg:"LU", luxemburg:"LU", luxemburgo:"LU", lussemburgo:"LU",
  mc:"MC", monaco:"MC", monako:"MC",
  us:"US", usa:"US", "etats-unis":"US", "etats unis":"US", "united states":"US",
  ca:"CA", canada:"CA", kanada:"CA",
  ar:"AR", argentine:"AR", argentina:"AR", argentinien:"AR",
  br:"BR", bresil:"BR", brasil:"BR", brazil:"BR", brasilien:"BR",
};

export function countryCode(pays) {
  const k = String(pays == null ? "" : pays)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ").trim().toLowerCase();
  if (!k) return "";
  return PAYS_VERS_CODE[k] || "";
}

function seoUrl(prefix, label, id) {
  const slug = slugify(label);
  return SHARE_ORIGIN + prefix + (slug ? slug + "-" : "") + id;
}

// Annonce (shop compris) : /a/<slug-du-titre>-<uuid>
export function adShareUrl(ad) {
  return seoUrl("/a/", ad && ad.title, ad && ad.id);
}

// Fiche de l'annuaire Pro : /p/<slug-du-nom>-<uuid>
export function proShareUrl(pro) {
  return seoUrl("/p/", pro && pro.name, pro && pro.id);
}

// Randonnee : /r/<slug-du-titre>-<uuid> (page SEO, prod-only comme les autres).
export function trailShareUrl(trail) {
  return seoUrl("/r/", trail && trail.title, trail && trail.id);
}

// Trajet HorseRoad : /t/<slug>-<uuid>.
// ⚠️ Le slug decrit le TRAJET ("transport-chevaux-lausanne-geneve")
// et ne recopie pas le titre libre du membre — il doit correspondre
// a ce que genere api/t/[id].js, sinon le serveur redirige.
export function tripShareUrl(trip) {
  if (!trip) return SHARE_ORIGIN;
  const verb = trip.trip_type === "request" ? "recherche transport chevaux" : "transport chevaux";
  const label = [verb, trip.departure_city, trip.arrival_city].filter(Boolean).join(" ");
  return seoUrl("/t/", label, trip.id);
}
